import { ApiError } from '@/teoCore/errors/ApiError';
import { FlatStore } from '@/teoCore/state/FlatState';
import { TeoCore } from '@/teoCore/TeoCore';

import type { TeoText } from '../TeoText';
import {
  areAllPromptOptionsSelected,
  areSelectedOptionsEqual,
  extractSelectedOptionsFromPromptTemplate,
} from '../TeoText.helpers';
import {
  GENERATOR_MAX_FILES,
  GENERATOR_MAX_TAB_BYTES,
  toApiAttachedFile,
  type GeneratorAttachedFile,
} from './GeneratorAttachedFile';
import {
  arrayBufferToBase64,
  hashFileContent,
  normalizeGeneratorMimeType,
  totalFilesByIdBytes,
  validateGeneratorFileSize,
} from './generatorFileUtils';
import { GeneratorHistoryEntry } from './GeneratorHistoryEntry';
import type { GeneratorStoredState } from './GeneratorStore.type';

export type AddAttachedFileResult = { ok: true } | { ok: false; message: string };

export class GeneratorStore extends FlatStore<GeneratorStore>() {
  history: GeneratorHistoryEntry[];
  selectedHistory: number;
  loading: boolean;
  filesById: Record<string, GeneratorAttachedFile>;

  #text: TeoText;

  constructor(text: TeoText, initialState: Partial<GeneratorStoredState> = {}) {
    super();
    this.#text = text;
    this.history =
      initialState.history?.length ?
        initialState?.history?.map((h) => new GeneratorHistoryEntry(h))
      : [new GeneratorHistoryEntry()];
    this.selectedHistory = initialState?.selectedHistory ?? 0;
    this.loading = false;
    this.filesById = { ...(initialState.filesById ?? {}) };
    this.addEventListener('*', () => this.#text.save());
    this.addEventListener('selectedHistory', () => {
      if (this.currentHistory?.textData) {
        this.#text.updateTextData(this.currentHistory.textData, { preserveDomSelection: true });
      }
    });
    text.store.addEventListener('currentTemplateId', (newTemplateId) => {
      if (
        this.#text.store.state.view === 'text-generator' &&
        this.currentHistory.locked &&
        this.currentHistory.textData?.storeState?.currentTemplateId !== newTemplateId
      ) {
        this.createNewCopy({ textData: this.#text.getTextData() });
      }
    });
    text.store.addEventListener('selectedPromptOptions', (newSelectedOptions) => {
      if (
        this.#text.store.state.view === 'text-generator' &&
        this.currentHistory.locked &&
        !areSelectedOptionsEqual(
          this.currentHistory.textData?.storeState?.selectedPromptOptions,
          newSelectedOptions,
        )
      ) {
        this.createNewCopy({ textData: this.#text.getTextData() });
      }
    });
  }

  get historyPageInfo() {
    return {
      total: this.history.length,
      current: this.selectedHistory,
    };
  }

  get currentHistory() {
    const currentHistory = this.history[this.selectedHistory];
    if (!currentHistory) {
      throw new Error('Error getting current history');
    }
    return currentHistory;
  }

  get currentAttachedFiles(): GeneratorAttachedFile[] {
    return this.currentHistory.attachedFileIds
      .map((id) => this.filesById[id])
      .filter((f): f is GeneratorAttachedFile => f !== undefined);
  }

  async addAttachedFile(file: File): Promise<AddAttachedFileResult> {
    const mimeType = normalizeGeneratorMimeType(file);
    if (!mimeType) {
      return { ok: false, message: 'Dateityp nicht unterstützt (PDF, Bild oder TXT).' };
    }

    const sizeError = validateGeneratorFileSize(file.size);
    if (sizeError) {
      return { ok: false, message: sizeError };
    }

    const entry = this.currentHistory;
    if (entry.attachedFileIds.length >= GENERATOR_MAX_FILES) {
      return { ok: false, message: `Maximal ${GENERATOR_MAX_FILES} Dateien.` };
    }

    const buffer = await file.arrayBuffer();
    const id = await hashFileContent(buffer);

    if (entry.attachedFileIds.includes(id)) {
      return { ok: true };
    }

    const existing = this.filesById[id];
    if (!existing) {
      const projectedTotal = totalFilesByIdBytes(this.filesById) + file.size;
      if (projectedTotal > GENERATOR_MAX_TAB_BYTES) {
        return {
          ok: false,
          message: 'Speicherlimit für Anhänge in diesem Text erreicht (max. 50 MB).',
        };
      }
      if (this.currentHistory.locked) {
        this.createNewCopy();
      }
      this.setStateValue('filesById', {
        ...this.filesById,
        [id]: {
          id,
          name: file.name,
          mimeType,
          size: file.size,
          dataBase64: arrayBufferToBase64(buffer),
        },
      });
    } else {
      existing.name = file.name;
      existing.mimeType = mimeType;
      this.setStateValue('filesById', { ...this.filesById }, true);
    }

    if (this.currentHistory.locked) {
      this.createNewCopy();
    }
    this.currentHistory.attachedFileIds = [...entry.attachedFileIds, id];
    this.setStateValue('history', this.history, true);
    return { ok: true };
  }

  removeAttachedFile(fileId: string): void {
    if (this.currentHistory.locked) {
      this.createNewCopy();
    }
    const entry = this.currentHistory;
    entry.attachedFileIds = entry.attachedFileIds.filter((id) => id !== fileId);
    this.removeFileReference(fileId);
    this.setStateValue('history', this.history, true);
  }

  private removeFileReference(fileId: string): void {
    if (!this.history.some((e) => e.attachedFileIds.includes(fileId))) {
      const next = { ...this.filesById };
      delete next[fileId];
      this.setStateValue('filesById', next);
    }
  }

  selectHistory(selectedHistory: number | GeneratorHistoryEntry) {
    if (typeof selectedHistory === 'number') {
      if (selectedHistory === this.selectedHistory) {
        return;
      }
      if (selectedHistory < 0 || selectedHistory >= this.history.length) {
        this.setStateValue('selectedHistory', 0);
      } else {
        this.setStateValue('selectedHistory', selectedHistory);
      }
    } else {
      this.selectHistory(this.history.indexOf(selectedHistory));
    }
  }

  async generate() {
    const textData = this.#text.getBaseTextData();
    const original = this.currentHistory;
    // make sure current textdata is set on the eventually "old" entry.
    original.textData = textData;
    // if it is already locked, create a new copy
    const entry =
      original.locked ?
        this.createNewCopy({
          generatorError: null,
        })
      : original;

    try {
      if (entry.locked) {
        throw new Error('Assert: currentHistory may not be locked for text generation.');
      }
      // call generator api
      const templateId = textData.storeState?.currentTemplateId;
      const selectedOptions = textData.storeState?.selectedPromptOptions;
      if (!templateId) {
        throw new Error('Konfiguration ungültig – Keine ID.');
      }
      const template = TeoCore.current.dynamicConfig.getTemplateById(templateId);
      if (!template) {
        throw new Error('Assert: template is required for text generation');
      }
      if (!selectedOptions || !areAllPromptOptionsSelected(template, selectedOptions)) {
        throw new Error('Konfiguration ungültig – Optionen nicht vollständig.');
      }
      this.setStateValue('loading', true);
      const attachedFiles = this.currentAttachedFiles.map(toApiAttachedFile);
      const result = await TeoCore.current.api.getAiTextGeneration({
        evaluation_profile_id: TeoCore.current.staticConfig.evaluationProfile,
        template_id: templateId,
        text_content_info: entry.instructions,
        selected_options: extractSelectedOptionsFromPromptTemplate(template, selectedOptions),
        attached_files: attachedFiles.length > 0 ? attachedFiles : undefined,
      });
      if (ApiError.matches(result)) {
        throw result;
      }
      const newText = result.text ?? '';
      this.#text.isEditable = true;
      this.#text.updateText(newText);
      this.#text.isEditable = false;
      this.#text.events.triggerEvent('textChanged', newText);
      this.#text.analysisConfiguration = result.analysisConfig;
      this.#text.updateResult(result.analysisResult, result.text);
      entry.textData = this.#text.getTextData();
      this.#text.events.triggerEvent('textChanged', newText);
    } catch (error) {
      entry.generatorError = {
        message: error instanceof Error ? error.message : 'Unbekannter Fehler',
      };
    } finally {
      this.setStateValue('loading', false);
    }

    entry.locked = !entry.generatorError;
    this.setStateValue('history', this.history, true);
    return entry;
  }

  /**
   * Keeps the entry of the shown text in step with changes that were made to it after it
   * was generated, so that leaving the entry and coming back does not undo them.
   */
  storeCurrentTextData() {
    this.currentHistory.textData = this.#text.getBaseTextData();
    this.setStateValue('history', this.history, true);
  }

  toJSON(): GeneratorStoredState {
    return {
      history: this.history,
      selectedHistory: this.selectedHistory,
      filesById: { ...this.filesById },
    };
  }

  updateInstructions(instructions: string) {
    if (this.currentHistory.locked) {
      this.createNewCopy({ instructions });
    } else {
      this.currentHistory.instructions = instructions;
      this.setStateValue('history', this.history, true);
    }
  }

  createNewCopy(changes?: Partial<GeneratorHistoryEntry>) {
    const original = this.currentHistory;
    const newHistory = original.clone();
    newHistory.locked = changes?.locked ?? false;
    newHistory.generatorError = changes?.generatorError ?? null;
    if (changes?.instructions) {
      newHistory.instructions = changes.instructions;
    }
    if (changes?.attachedFileIds) {
      newHistory.attachedFileIds = [...changes.attachedFileIds];
    }
    if (changes?.textData) {
      newHistory.textData = changes.textData;
    }
    const currentHistories = this.history;
    this.setStateValue('history', [...currentHistories, newHistory]);
    this.setStateValue('selectedHistory', currentHistories.length);
    return newHistory;
  }
}
